import { useCallback, useState } from "react";

import {
  AvatarIcon,
  Command,
  DropdownMenu,
  SelectButton,
  Text,
} from "@vericus/cadmus-ui";

import { Row } from "@tanstack/table-core";

import { ProjectIssueInput } from "@/generated/graphql";

import { useUpdateProjectIssue } from "../../hooks/use-update-project-issue";
import { ProjectIssueWithChildren } from "../../types";
import { useProjectManagementTableContext } from "../table/context";
import * as styles from "./styles.css";

interface AssigneeDropdownProps {
  row: Row<ProjectIssueWithChildren>;
}

export const AssigneeDropdown = ({ row }: AssigneeDropdownProps) => {
  const { id: issueId, assignees } = row.original;

  const [open, setOpen] = useState(false);

  const { group } = useProjectManagementTableContext();

  const updateProjectIssue = useUpdateProjectIssue(group.id);

  const handleUpdateAssignee = useCallback(
    (assigneeId: string | null) =>
      updateProjectIssue([
        {
          id: issueId,
          assignees: assigneeId ? [{ userId: assigneeId }] : [],
        },
      ] as ProjectIssueInput[]),
    [issueId, updateProjectIssue]
  );

  const currentAssignee = group.members.find((a) => a.id === assignees.at(0));

  return (
    <DropdownMenu.Root open={open} onOpenChange={(open) => setOpen(open)}>
      <DropdownMenu.Trigger asChild>
        <SelectButton
          className={styles.assigneeSelectButton}
          icon={
            <AvatarIcon
              name={currentAssignee ? currentAssignee.name : "Unassigned"}
              size="md"
              shade={
                (currentAssignee &&
                  group.members.map((a) => a.id).indexOf(currentAssignee.id)) ||
                0
              }
            />
          }
        >
          <Text kind="headingSix" inline as="span">
            {currentAssignee ? currentAssignee.name : "Unassigned"}
          </Text>
        </SelectButton>
      </DropdownMenu.Trigger>
      <DropdownMenu.Content>
        <Command.Root style={{ height: "100%", width: "100%" }}>
          <Command.List>
            <Command.Group>
              {[...group.members, { id: null, name: null }].map((item) => (
                <Command.RadioItem
                  key={item.id}
                  value={item.id ?? undefined}
                  onSelect={() => {
                    handleUpdateAssignee(item.id);
                    setOpen(false);
                  }}
                  data-selected={
                    currentAssignee && currentAssignee.id === item.id
                      ? "true"
                      : undefined
                  }
                >
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "row",
                      alignItems: "center",
                      gap: 12,
                      padding: "8px",
                    }}
                  >
                    <AvatarIcon
                      name={item.name ?? undefined}
                      size="md"
                      shade={
                        item.id
                          ? group.members.map((a) => a.id).indexOf(item.id)
                          : 0
                      }
                    />
                    {item.name ?? "Unassign"}
                  </div>
                </Command.RadioItem>
              ))}
            </Command.Group>
          </Command.List>
        </Command.Root>
      </DropdownMenu.Content>
    </DropdownMenu.Root>
  );
};
